import React from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TAddProjectFormProps } from '@/console/features/projects/components/project-drawer/add-project-form.types';
import {
  TAddProjectInputSchema,
  TProjectInputSchema,
} from '@/console/features/projects/components/project-drawer/add-project.types';
import { ProjectSelect } from '@/console/features/projects/components/project-select/project-select';
import { useProjectSelectOptions } from '@/console/features/projects/hooks/use-project-select-options/use-project-select-options';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { SelectedItemsList } from '@/shared/components/ui/selected-items-list/selected-items-list';
import { ProjectForSelectFragment } from '@/shared/types/types.generated';

export const AddProjectForm: React.FC<TAddProjectFormProps> = ({
  formMethods,
  onClose,
  groupId,
}) => {
  const { t } = useTranslation();
  const { setValue, control, trigger } = formMethods;
  const { createProjectOption } = useProjectSelectOptions({
    additionalInfo: { vendor: true, ide: true },
  });

  const projects = useWatch({
    control,
    name: 'projects',
    defaultValue: [],
  });

  const getSelectedItems = () => {
    return projects?.map((project) => ({
      icon: <ProjectsIcon />,
      name: project.name,
      onDelete: () => {
        setValue(
          'projects',
          projects?.filter(({ projectId }) => projectId !== project.projectId),
        );
        void trigger('projects');
      },
    }));
  };

  const projectFilter = (project: ProjectForSelectFragment) =>
    !groupId || project?.groupId !== groupId;

  return (
    <FormContainer justifyContent="space-between">
      <FormContent>
        <ProjectSelect<TAddProjectInputSchema, TProjectInputSchema>
          name="projects"
          projectFilter={projectFilter}
          createOption={createProjectOption}
          getItemId={(value) => value?.projectId}
          required
          optionProps={{ additionalInfo: { vendor: true, ide: true } }}
        />
        <SelectedItemsList
          title={t('projects:selected_projects')}
          selectedItems={getSelectedItems()}
        />
      </FormContent>
      <Submit onClose={onClose} submitButtonLabel={t('common:add')} />
    </FormContainer>
  );
};
